"use client";

import {
  ArrowRight,
  CalendarDays,
  CreditCard,
  House,
  Menu,
  UsersRound,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { FaFacebookF, FaInstagram, FaSnapchatGhost } from "react-icons/fa";

import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { club } from "@/data/club";

function Brand({ menu = false }: { menu?: boolean }) {
  return (
    <Link
      aria-label="Imana Academy — retour à l’accueil"
      className={`group flex min-w-0 items-center ${menu ? "w-full justify-center" : ""}`}
      href="/#accueil"
    >
      <span className={`relative aspect-[2172/724] overflow-hidden ${menu ? "w-full max-w-[320px]" : "w-[220px] max-[389px]:w-[205px] sm:w-[230px]"}`}>
        <Image
          preload={!menu}
          alt="Imana Academy"
          className={`object-contain drop-shadow-[0_1px_1px_rgba(0,0,0,.75)] ${menu ? "scale-[1.08]" : ""}`}
          fill
          sizes="(max-width: 639px) 164px, 190px"
          src="/generated/imana-wordmark.png"
        />
      </span>
    </Link>
  );
}

function DesktopNavigation() {
  return (
    <nav aria-label="Navigation principale" className="hidden items-center gap-1 xl:flex">
      {club.nav.slice(1).map((item) => (
        <Link
          key={item.label}
          className="rounded-[.2rem] px-3 py-2 text-[.68rem] font-extrabold uppercase tracking-[.1em] text-white/75 transition-colors hover:bg-white/8 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          href={`/${item.href}`}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

function MobileNavigation() {
  const icons = [House, UsersRound, CalendarDays, CreditCard];

  return (
    <nav aria-label="Navigation mobile" className="mt-2 flex flex-none flex-col gap-0.5 sm:mt-2 sm:gap-1">
      <p className="mb-3 px-2 text-[.58rem] font-extrabold uppercase tracking-[.18em] text-muted-foreground">Navigation</p>
      {club.nav.map((item, index) => {
        const Icon = icons[index];

        return (
        <SheetClose asChild key={item.label}>
          <Link
            className="group flex min-h-13 items-center gap-3 px-2 py-1.5 text-foreground transition-colors hover:bg-white/[.05] hover:text-primary"
            href={`/${item.href}`}
          >
            <span className="grid size-9 shrink-0 place-items-center border border-white/12 bg-white/[.02] transition-colors group-hover:border-primary/60 group-hover:bg-primary/10">
              <Icon className="size-[1.05rem] stroke-[1.65] text-primary" />
            </span>
            <span className="font-display text-[1.08rem] font-black uppercase tracking-[.01em]">
              {item.label}
            </span>
            <ArrowRight className="ml-auto size-4 text-muted-foreground/60 transition-transform group-hover:translate-x-1 group-hover:text-primary" />
          </Link>
        </SheetClose>
        );
      })}
    </nav>
  );
}

function SiteHeader() {
  return (
    <header className="pointer-events-none fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-5 sm:pt-4 lg:px-8">
      <div className="site-nav pointer-events-auto relative mx-auto flex h-[4.35rem] w-full max-w-[1480px] items-center gap-2 bg-black/65 px-2.5 text-white backdrop-blur-3xl sm:h-[4.65rem] sm:gap-3 sm:px-4 lg:px-5">
        <div className="absolute left-4 top-1/2 z-10 -translate-y-1/2 xl:static xl:order-4 xl:translate-y-0">
          <Sheet>
            <SheetTrigger asChild>
              <Button aria-label="Ouvrir le menu" className="border border-white/12 text-white hover:border-primary/60 hover:bg-primary/10 hover:text-white xl:hidden" size="icon" variant="ghost">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              className="h-[100dvh] max-h-[100dvh] w-[min(82vw,320px)] gap-0 overflow-y-auto overscroll-contain border-r border-white/15 bg-background/82 px-3 pb-[max(.75rem,env(safe-area-inset-bottom))] pl-[max(.75rem,env(safe-area-inset-left))] pt-[max(.75rem,env(safe-area-inset-top))] [-webkit-overflow-scrolling:touch] backdrop-blur-2xl sm:w-[min(52vw,340px)] sm:max-w-none sm:gap-0 sm:px-7 sm:pb-[max(1.75rem,env(safe-area-inset-bottom))] sm:pl-[max(1.75rem,env(safe-area-inset-left))] sm:pt-[max(1.75rem,env(safe-area-inset-top))]"
              closeButton={false}
              side="left"
            >
              <div className="flex min-h-[4.5rem] shrink-0 items-center justify-center">
                <SheetTitle className="sr-only">Menu Imana Academy</SheetTitle>
                <Brand menu />
              </div>
              <MobileNavigation />
              <div className="mobile-menu-cta relative mb-3 mt-7 flex min-h-[380px] flex-1 flex-col overflow-hidden border border-[#d34a5d]/55 bg-[#9d2036]/55 px-5 py-6 text-white shadow-[0_16px_44px_rgba(126,20,39,.22)] backdrop-blur-lg sm:mt-8">
                <div aria-hidden="true" className="animated-grid pointer-events-none absolute inset-0 opacity-15 [background-image:linear-gradient(rgba(255,255,255,.32)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.32)_1px,transparent_1px)] [background-size:28px_28px]" />
                <div className="relative flex h-full flex-1 flex-col">
                  <p className="text-[.58rem] font-extrabold uppercase tracking-[.18em] text-white/72">Première séance</p>
                  <p className="mt-2 font-display text-3xl font-black uppercase leading-[.92]">Ton premier cours<br />est offert.</p>
                  <p className="mt-4 max-w-xs text-sm leading-6 text-white/78">Viens rencontrer l’équipe et découvrir la discipline qui te correspond.</p>
                  <SheetClose asChild>
                    <Button asChild className="mt-auto h-12 w-full border-white/70 bg-white text-black shadow-[0_8px_20px_rgba(0,0,0,.16)] hover:bg-white/90" size="xl">
                      <Link href="/inscription">Réserver maintenant <ArrowRight /></Link>
                    </Button>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <div className="absolute left-1/2 -translate-x-1/2 xl:static xl:translate-x-0">
          <Brand />
        </div>
        <div className="ml-auto flex items-center gap-1.5 xl:ml-8 xl:flex-1 xl:justify-end">
          <DesktopNavigation />
          <div className="mx-1 hidden h-6 w-px bg-border xl:block" />
          <ThemeToggle />
          <Button asChild className="hidden whitespace-nowrap md:inline-flex" size="lg">
            <Link href="/inscription">
              S’inscrire
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

function SocialDock() {
  const networks = [
    {
      label: "Snapchat",
      href: club.snapchat,
      icon: FaSnapchatGhost,
      iconClassName: "drop-shadow-[0_1px_1px_rgba(0,0,0,.72)]",
      className: "bg-[#ffdf00] hover:bg-[#ffe52f]",
    },
    {
      label: "Instagram",
      href: club.instagram,
      icon: FaInstagram,
      iconClassName: "drop-shadow-[0_1px_1px_rgba(0,0,0,.3)]",
      className: "bg-[linear-gradient(135deg,#7137a8,#cf326c_55%,#eb6a34)] hover:brightness-110",
    },
    {
      label: "Facebook",
      href: club.facebook,
      icon: FaFacebookF,
      iconClassName: "drop-shadow-[0_1px_1px_rgba(0,0,0,.3)]",
      className: "bg-[#1877f2] hover:bg-[#378bf4]",
    },
  ];

  return (
    <aside aria-label="Réseaux sociaux" className="social-dock fixed bottom-[max(.75rem,env(safe-area-inset-bottom))] right-3 z-40 flex flex-col items-center bg-black/30 px-2 py-2 text-white shadow-[0_8px_24px_rgba(0,0,0,.2)] backdrop-blur-md sm:bottom-4 sm:right-4">
      <p className="mb-1.5 whitespace-nowrap text-center text-[.46rem] font-extrabold uppercase tracking-[.11em] text-white">
        Suivez-nous sur nos réseaux
      </p>
      <div className="flex w-full items-center justify-center gap-1.5">
        {networks.map(({ label, href, icon: Icon, iconClassName, className }) => (
          <a
            aria-label={label}
            className={`flex size-9 items-center justify-center rounded-[.48rem] text-white shadow-[0_3px_10px_rgba(0,0,0,.18)] transition-[transform,filter,background-color] hover:-translate-y-0.5 ${className}`}
            href={href}
            key={label}
            rel="noreferrer"
            target="_blank"
          >
            <Icon aria-hidden="true" className={`block size-[1.05rem] shrink-0 ${iconClassName}`} />
          </a>
        ))}
      </div>
    </aside>
  );
}

export function ClubShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <a
        className="fixed left-4 top-4 z-[60] -translate-y-24 rounded-[.3rem] bg-primary px-4 py-3 text-xs font-bold text-primary-foreground transition-transform focus:translate-y-0"
        href="#contenu"
      >
        Aller au contenu
      </a>
      <SiteHeader />
      <SocialDock />
      <main id="contenu">{children}</main>
    </div>
  );
}
